import { ActionEvents } from '@griply/types';
import { taskTypeToTrackingTaskType } from '@griply/utils';
import { useCallback, useEffect, useRef } from 'react';
import { useUpdateGoalMutation } from 'shared/hooks/queries/use-update-goal-mutation';
import { useUpdateTaskMutation } from 'shared/hooks/queries/use-update-task-mutation';
import { useTrackEvents } from 'shared/hooks/use-track-events';
import { getGoalDocById } from 'shared/services/backend-api';
import { ID } from 'shared/types/id';
import { Task } from 'shared/types/task';
import { TaskActionType } from 'shared/types/task-action';
import { TaskType } from 'shared/types/task-base';

import { useTaskById } from './use-task-by-id';

export const useDeleteTask = (parentTaskId?: ID) => {
  const deleteTaskRef = useRef<Task>();
  const parentTask = useTaskById(parentTaskId);

  const track = useTrackEvents();
  const {
    mutate: updateTask,
    isSuccess,
    reset,
    error,
    isPending,
  } = useUpdateTaskMutation();
  const { mutate: updateGoal } = useUpdateGoalMutation();

  const submit = useCallback(
    async (task: Task) => {
      deleteTaskRef.current = task;

      // filter the task that will be deleted from the parent task.
      if (parentTask) {
        updateTask({
          id: parentTask.id,
          childIds: parentTask.childIds?.filter((taskId) => taskId !== task.id),
        });
      }

      try {
        const goal =
          task.goalId && task.action?.type !== TaskActionType.GoalTimeslot
            ? await getGoalDocById(task.goalId)
            : undefined;

        if (goal) {
          updateGoal({
            goal: {
              id: goal.id,
              completedTaskCount: task.completedAt ? -1 : 0,
              taskCount: -1,
              sections: goal.sections?.map((section) => ({
                ...section,
                tasks: section.tasks.filter((id) => id !== task.id),
              })),
            },
          });
        }
      } catch (e) {
        // do nothing, if the goal is not reachable, it probably means it is already deleted.
        // Otherwise, the BE will take care of it.
        // this implementation was requested by Ruud
      }

      task.childIds?.forEach((id) => {
        updateTask({ id, deletedAt: new Date() });
        track(ActionEvents.TaskDelete, {
          type: taskTypeToTrackingTaskType[TaskType.Task],
        });
      });

      updateTask({ id: task.id, deletedAt: new Date() });
    },
    [updateGoal, updateTask, parentTask, track],
  );

  const retry = useCallback(() => {
    const task = deleteTaskRef.current;
    if (task) {
      updateTask({ id: task.id, deletedAt: new Date() });
    }
  }, [updateTask]);

  const resetAll = useCallback(() => {
    reset();
    deleteTaskRef.current = undefined;
  }, [reset]);

  useEffect(() => {
    if (isSuccess && deleteTaskRef.current) {
      track(ActionEvents.TaskDelete, {
        type: taskTypeToTrackingTaskType[deleteTaskRef.current.type],
      });
    }
  }, [isSuccess, track]);

  return {
    submit,
    retry,
    reset: resetAll,
    error,
    isLoading: isPending,
    isSuccess,
  };
};
